'use client'

import { useState, useEffect } from 'react'
import { getPromoCampaign, type PromoCampaignPublic } from '@/lib/api'
import {
  getStoredPromoSlug,
  shouldSyncSignupAccessFromStorageEvent,
  SIGNUP_ACCESS_STORED_EVENT,
} from '@/lib/signupAccessStorage'

// Module-level cache so multiple consumers on one page share a single fetch
// per slug. TTL-bounded so a mount/SPA-navigation (which fires no window
// `focus`) can't reuse a campaign whose promo flag went stale after an ops
// flip — the strike self-heals within the TTL on the next mount.
const CAMPAIGN_CACHE_TTL_MS = 60_000
let cachedSlug: string | null = null
let cachedCampaign: PromoCampaignPublic | null = null
let cachedAt = 0

/**
 * The active promo campaign behind the visitor's stored promo slug, for
 * marketing surfaces shown before signup. Resolves to null when no slug is
 * stored, the campaign is no longer redeemable, or the lookup fails —
 * consumers silently render their non-promo state. Starts null so statically
 * prerendered pages hydrate cleanly, and re-syncs on the same signals as
 * useStoredSignupAccess so the deal display can't lag behind the CTAs.
 */
export function useStoredPromoCampaign(): { campaign: PromoCampaignPublic | null } {
  const [campaign, setCampaign] = useState<PromoCampaignPublic | null>(null)

  useEffect(() => {
    let cancelled = false

    // forceRefetch bypasses the cache entirely (window focus); otherwise the
    // cache is reused only within its TTL, so a backend flag flip (e.g.
    // FIRST_MONTH_PROMO_REWARD_ID unset) can't leave a stale
    // firstMonthPromoActive advertising a discount checkout won't apply.
    const sync = (forceRefetch = false) => {
      const slug = getStoredPromoSlug()
      if (!slug) {
        setCampaign(null)
        return
      }

      const cacheFresh = cachedSlug === slug && Date.now() - cachedAt < CAMPAIGN_CACHE_TTL_MS
      const lookup =
        !forceRefetch && cacheFresh
          ? Promise.resolve(cachedCampaign)
          : getPromoCampaign(slug).then(data => {
              cachedSlug = slug
              cachedCampaign = data
              cachedAt = Date.now()
              return data
            })

      lookup
        .then(data => {
          // Ignore late resolutions: apply only if the stored slug still
          // matches what this sync run fetched.
          if (!cancelled && getStoredPromoSlug() === slug) setCampaign(data?.active ? data : null)
        })
        .catch(() => {
          // Network failure — fall back to the non-promo state rather than
          // keeping copy from a previously loaded campaign.
          if (!cancelled && getStoredPromoSlug() === slug) setCampaign(null)
        })
    }

    // Wrappers so the DOM Event arg never lands in forceRefetch. Mount/storage/
    // referral-stored use the cache (dedupe multiple consumers); focus
    // revalidates so a flag flip is picked up on the next tab return.
    const syncFromCache = () => sync(false)
    const revalidate = () => sync(true)
    const handleStorage = (event: StorageEvent) => {
      if (shouldSyncSignupAccessFromStorageEvent(event.key)) syncFromCache()
    }

    sync(false)
    window.addEventListener(SIGNUP_ACCESS_STORED_EVENT, syncFromCache)
    window.addEventListener('storage', handleStorage)
    window.addEventListener('focus', revalidate)

    return () => {
      cancelled = true
      window.removeEventListener(SIGNUP_ACCESS_STORED_EVENT, syncFromCache)
      window.removeEventListener('storage', handleStorage)
      window.removeEventListener('focus', revalidate)
    }
  }, [])

  return { campaign }
}
